import { useCallback } from "react";
import { typesListName } from "src/api/config";
import { FiltersStateType, Keys } from "src/pages/DashboardPage/acadia/type";
import { CheckBoxListItem } from "src/types/main";

type Path = {
  Title: string;
  ID: number;
} | null;

type DependencyItemType = Record<string, Path>;

const keysMap: Record<Keys, string> = {
  collections: "Collection",
  departments: "Department",
  sectors: "Sector",
  processes: "Process",
  types: typesListName,
};

// function generateCombinationsWithLodash(inputData: Record<string, CheckBoxListItem[]>) {
//   // Получаем массивы элементов для каждой категории или [null], если категория пуста
//   const categories = _.mapValues(inputData, (items) => (_.isEmpty(items) ? [null] : items));

//   // Функция для создания комбинаций
//   function createCombinations(categories: Record<string, CheckBoxListItem[] | null[]>) {
//     let keys = _.keys(categories).map((k) => keysMap[k as Keys]);
//     let values = _.values(categories).map((category) =>
//       category.map((item) => (item ? { ID: item.id, Title: item.title } : null))
//     );

//     // Генерация всех возможных комбинаций с помощью рекурсивной функции
//     function combine(index: number, paths: Path[]): DependencyItemType[] {
//       if (index === keys.length) {
//         return [_.zipObject(keys, paths)];
//       }

//       return _.flatMap(values[index], (value) => combine(index + 1, [...paths, value]));
//     }

//     return combine(0, []);
//   }

//   return createCombinations(categories);
// }

function createKey(obj: any) {
  return `${obj.Collection?.ID || ""}-${obj.Collection?.Title || ""}|${obj.Department?.ID || ""}-${
    obj.Department?.Title || ""
  }|${obj.Process?.ID || ""}-${obj.Process?.Title || ""}|${obj.Sector?.ID || ""}-${obj.Sector?.Title || ""}`;
}

function againCompare(array1: any[], array2: any[]) {
  // Функция для создания ключа для хеш-таблицы

  // Преобразование второго массива в хеш-таблицу
  const hashTable = new Map();
  array2.forEach((item) => {
    const key = createKey(item);
    hashTable.set(key, item);
  });

  // Поиск совпадений
  const similarities = array1
    .filter((item1: any) => {
      // Адаптация структуры объекта из первого массива к формату ключа
      const adaptedItem = {
        Collection: item1.collections,
        Department: item1.departments,
        Process: item1.processes,
        Sector: item1.sectors,
      };

      const key = createKey(adaptedItem);
      return hashTable.has(key);
    })
    .map((item1: any) => ({
      item1, // Или любая другая логика объединения или обработки совпавших объектов
      item2: hashTable.get(
        createKey({
          Collection: item1.collections,
          Department: item1.departments,
          Process: item1.processes,
          Sector: item1.sectors,
        }),
      ),
    }));

  return similarities;
}

type Dependency = {
  ID: number;
  Collection: {
    Title: string;
    ID: number;
  };
  Department: {
    Title: string;
    ID: number;
  };
  Process: {
    Title: string;
    ID: number;
  };
  Sector: {
    Title: string;
    ID: number;
  };
};

const cascadeFilterKeys: Keys[] = ["collections", "departments", "sectors", "processes"];

export function useFilterDependenciesOptimized() {
  const filterDependenciesOptimized = useCallback(
    (filters: Pick<FiltersStateType, Keys>, dependencies: Dependency[]) => {
      const result: Record<Keys, CheckBoxListItem[]> = {
        collections: [],
        departments: [],
        sectors: [],
        processes: [],
        types: [],
      };

      const selectedByDepKey: Partial<Record<string, Set<number>>> = {};
      cascadeFilterKeys.forEach((filterKey) => {
        if (filters[filterKey].length > 0) {
          selectedByDepKey[keysMap[filterKey]] = new Set(filters[filterKey].map((item) => Number(item.id)));
        }
      });

      if (Object.keys(selectedByDepKey).length === 0) {
        return result;
      }

      const pushUnique = (filterKey: Keys, id: number, title: string) => {
        if (!result[filterKey].some((item) => Number(item.id) === id)) {
          result[filterKey].push({ id, title });
        }
      };

      // For list at level K: row must match every selected filter except K (AND on one dependency row).
      // Skipping K keeps other options at the same level visible for multi-select.
      cascadeFilterKeys.forEach((targetKey) => {
        const targetDepKey = keysMap[targetKey];

        const matchingDeps = dependencies.filter((dependency) =>
          Object.entries(selectedByDepKey).every(([depKey, ids]) => {
            if (depKey === targetDepKey) return true;
            const depItem = dependency[depKey as keyof Dependency];
            if (!depItem || typeof depItem === "number") return false;
            return ids?.has(Number(depItem.ID));
          }),
        );

        matchingDeps.forEach((dependency) => {
          const depItem = dependency[targetDepKey as keyof Dependency];
          if (!depItem || typeof depItem === "number") return;
          pushUnique(targetKey, Number(depItem.ID), depItem.Title);
        });
      });

      return result;
    },
    [],
  );

  return filterDependenciesOptimized;
}

/**
 * @param items it's array with objects
 * @param key it's what we trying to find
 *
 */
export const getUnique = (items: Record<any, any>[], key: string) => {
  const getItemsByKey = items.map((item) => item[key]);
  return [...new Set(getItemsByKey)];
};

export function isNull<T>(value: T | null | undefined, defaultValue: T): T {
  return value !== null && value !== undefined ? value : defaultValue;
}

export function isObjectWithKeys<T extends object>(obj: unknown): obj is T {
  return typeof obj === "object" && obj !== null && Object.keys(obj).length > 0;
}
